import type { Invoice } from "@nlc/shared-types";

import { ChevronDownIcon } from "lucide-react";
import {
  Disclosure,
  DisclosureGroup,
  DisclosurePanel,
  Heading,
} from "react-aria-components";

import { formatInvoiceDate, formatInvoiceTotal } from "../utils/invoice-format";
import { InvoiceActionButtons } from "./InvoiceActionButtons";
import { InvoiceDisclosureTrigger } from "./InvoiceDisclosureTrigger";
import { InvoiceStatusBadge } from "./InvoiceStatusBadge";

interface InvoicesListProps {
  invoices: Invoice[];
  onCopyLink: (invoice: Invoice) => void;
  onIssueCredit?: (invoice: Invoice) => void;
}

export function InvoicesList({
  invoices,
  onCopyLink,
  onIssueCredit,
}: InvoicesListProps) {
  return (
    <DisclosureGroup
      allowsMultipleExpanded
      className="flex flex-col divide-y divide-border-secondary"
    >
      {invoices.map((invoice) => (
        <Disclosure className="group px-3xl" id={invoice.id} key={invoice.id}>
          <Heading className="m-0">
            <InvoiceDisclosureTrigger className="items-center gap-md py-xl group-data-[expanded]:pb-0">
              <span className="text-sm font-semibold text-text-primary whitespace-nowrap">
                {formatInvoiceTotal(invoice.total, invoice.currency)}
              </span>
              <span className="flex-1 text-xs text-text-tertiary whitespace-nowrap">
                {formatInvoiceDate(invoice.date)}
              </span>
              <InvoiceStatusBadge status={invoice.status} />

              <ChevronDownIcon
                aria-hidden
                className="size-5 shrink-0 text-text-secondary transition-transform group-data-[expanded]:rotate-180"
              />
            </InvoiceDisclosureTrigger>
          </Heading>

          <DisclosurePanel>
            <div className="flex flex-col gap-lg pt-md pb-xl">
              <dl className="flex flex-col gap-lg">
                <div className="flex flex-col gap-xs">
                  <dt className="text-xs text-text-tertiary">Description</dt>
                  <dd className="text-sm text-text-primary">
                    {invoice.description}
                  </dd>
                </div>
                <div className="flex flex-col gap-xs">
                  <dt className="text-xs text-text-tertiary">Invoice ID</dt>
                  <dd className="text-sm text-text-primary break-all">
                    {invoice.id}
                  </dd>
                </div>
              </dl>
              <InvoiceActionButtons
                invoice={invoice}
                onCopyLink={onCopyLink}
                onIssueCredit={onIssueCredit}
              />
            </div>
          </DisclosurePanel>
        </Disclosure>
      ))}
    </DisclosureGroup>
  );
}
